/**
 * CreditRating — Notation crédit façon Bloomfield (onglet du module Financement).
 * Échelle AAA → D (20 crans), analyse IA pré-remplie selon la méthodologie
 * Bloomfield (5 typologies d'émetteurs), correspondance internationale.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import {
  Plus, X, Loader2, Sparkles, Trash2, Pencil, ChevronDown, ChevronUp,
  TrendingUp, TrendingDown, Minus, Building2, User, Search, Info, Gauge,
  FileDown, AlertTriangle,
} from "lucide-react";
import { Link } from "react-router-dom";

const NOTE_COLORS = {
  AAA: "#1D9E75", AA: "#3BB98A", A: "#6BC9A5", BBB: "#378ADD",
  BB: "#EF9F27", B: "#E8742C", "CCC-C": "#E24B4A", D: "#9B1C1C",
};

const OUTLOOK_META = {
  positive: { icon: TrendingUp, label: "Positive", color: "#1D9E75" },
  stable: { icon: Minus, label: "Stable", color: "#A1A1AA" },
  negative: { icon: TrendingDown, label: "Négative", color: "#E24B4A" },
};

function NoteBadge({ note, noteClass, size = "lg" }) {
  const color = NOTE_COLORS[noteClass] || "#A1A1AA";
  return (
    <span
      className={`inline-flex items-center justify-center rounded-md font-bold noam-display ${size === "lg" ? "text-xl px-3 py-1.5 min-w-[64px]" : "text-sm px-2 py-0.5 min-w-[44px]"}`}
      style={{ background: `${color}1f`, color, border: `1px solid ${color}55` }}
      data-testid={`rating-note-badge-${note}`}
    >
      {note}
    </span>
  );
}

/** Badge compact affiché sur la fiche Annuaire d'un acteur noté. */
export function CreditRatingBadge({ entityId }) {
  const [rating, setRating] = useState(null);
  useEffect(() => {
    if (!entityId) return;
    api.get(`/workspace/credit-rating?subject_id=${entityId}`)
      .then((r) => setRating((r.data.items || [])[0] || null))
      .catch(() => {});
  }, [entityId]);
  if (!rating) return null;
  const O = OUTLOOK_META[rating.outlook] || OUTLOOK_META.stable;
  return (
    <Link
      to="/app/financement"
      className="inline-flex items-center gap-1.5 shrink-0"
      title={`Note crédit ${rating.note} — ${rating.risk_label} · Perspective ${O.label}${rating.review_due ? " · Revue annuelle due" : ""}`}
      data-testid={`annuaire-credit-rating-badge-${entityId}`}
    >
      <NoteBadge note={rating.note} noteClass={rating.note_class} size="sm" />
      <O.icon className="w-3 h-3" style={{ color: O.color }} />
      {rating.review_due && <AlertTriangle className="w-3 h-3 text-[#EF9F27]" />}
    </Link>
  );
}

function ScalePanel({ scale }) {
  const [open, setOpen] = useState(false);
  if (!scale) return null;
  return (
    <div className="noam-card p-4">
      <button onClick={() => setOpen((v) => !v)} className="w-full flex items-center justify-between text-left" data-testid="rating-scale-toggle">
        <span className="text-sm font-semibold flex items-center gap-2"><Gauge className="w-4 h-4 text-[#378ADD]" /> Échelle de notation (20 crans · 8 classes) & correspondance internationale</span>
        {open ? <ChevronUp className="w-4 h-4 text-zinc-500" /> : <ChevronDown className="w-4 h-4 text-zinc-500" />}
      </button>
      {open && (
        <div className="mt-4 grid md:grid-cols-2 gap-5">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">Catégorie investissement (AAA → BBB-) · spéculative (BB+ → D)</div>
            <div className="flex flex-wrap gap-1.5">
              {scale.notes.map((n) => {
                const cls = n === "AAA" ? "AAA" : n.startsWith("AA") ? "AA" : n.startsWith("BBB") ? "BBB" : n.startsWith("A") ? "A" : n.startsWith("BB") ? "BB" : n.startsWith("B") ? "B" : n === "D" ? "D" : "CCC-C";
                return <NoteBadge key={n} note={n} noteClass={cls} size="sm" />;
              })}
            </div>
            <div className="mt-3 space-y-1">
              {Object.entries(scale.risk_labels).map(([cls, label]) => (
                <div key={cls} className="flex items-center gap-2 text-[11px]">
                  <span className="w-2 h-2 rounded-full" style={{ background: NOTE_COLORS[cls] }} />
                  <span className="text-zinc-400 w-14 font-mono">{cls}</span>
                  <span className="text-zinc-500">{label}</span>
                </div>
              ))}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">Correspondance indicative Fitch / Moody's / S&P</div>
            <table className="w-full text-[11px]">
              <tbody>
                {scale.correspondence.map((c) => (
                  <tr key={c.bloomfield} className="border-b border-white/5">
                    <td className="py-1.5 text-zinc-300 font-medium">{c.bloomfield}</td>
                    <td className="py-1.5 text-zinc-500 text-right">{c.international}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function RatingModal({ scale, initial, onClose, onSaved }) {
  const isEdit = !!initial?.id;
  const [subjectType, setSubjectType] = useState(initial?.subject_type || "org");
  const [subjectId, setSubjectId] = useState(initial?.subject_id || null);
  const [subjectName, setSubjectName] = useState(initial?.subject_name || "");
  const [issuerCategory, setIssuerCategory] = useState(initial?.issuer_category || "entreprise");
  const [note, setNote] = useState(initial?.note || "");
  const [outlook, setOutlook] = useState(initial?.outlook || "stable");
  const [justification, setJustification] = useState(initial?.justification || "");
  const [criteria, setCriteria] = useState(initial?.criteria || []);
  const [context, setContext] = useState(initial?.context || "");
  const [intlEq, setIntlEq] = useState("");
  const [dataGaps, setDataGaps] = useState([]);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [assessing, setAssessing] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (subjectType !== "annuaire" || query.length < 2) { setResults([]); return; }
    const t = setTimeout(() => {
      api.get(`/workspace/annuaire/entities?search=${encodeURIComponent(query)}`)
        .then((r) => setResults((r.data.items || r.data || []).slice(0, 6)))
        .catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [query, subjectType]);

  const runAssess = async () => {
    if (assessing) return;
    if (subjectType === "annuaire" && !subjectId) { notify.error("Sélectionnez un acteur de l'Annuaire."); return; }
    if (subjectType === "libre" && !subjectName.trim()) { notify.error("Indiquez le nom du sujet à noter."); return; }
    setAssessing(true);
    try {
      const r = await api.post("/workspace/credit-rating/ai-assess", {
        subject_type: subjectType, subject_id: subjectId, subject_name: subjectName.trim(),
        issuer_category: issuerCategory, context: context.trim(),
      });
      setNote(r.data.note);
      setOutlook(r.data.outlook || "stable");
      setJustification(r.data.justification || "");
      setCriteria(r.data.criteria || []);
      setIntlEq(r.data.international_equivalent || "");
      setDataGaps(r.data.data_gaps || []);
      if (!subjectName.trim()) setSubjectName(r.data.subject_name || "");
      notify.success(`Analyse IA : note suggérée ${r.data.note} (${r.data.risk_label}). Vous pouvez l'ajuster.`);
    } catch (e) { notify.error(fmtError(e)); }
    finally { setAssessing(false); }
  };

  const save = async () => {
    if (!note) { notify.error("Choisissez une note (ou lancez l'analyse IA)."); return; }
    const name = subjectName.trim() || (subjectType === "org" ? "Mon organisation" : "");
    if (!name) { notify.error("Nom du sujet requis."); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await api.patch(`/workspace/credit-rating/${initial.id}`, { note, outlook, justification, criteria, context });
      } else {
        await api.post("/workspace/credit-rating", {
          subject_type: subjectType, subject_id: subjectId, subject_name: name,
          issuer_category: issuerCategory, note, outlook, justification, criteria, context,
          international_equivalent: intlEq,
        });
      }
      notify.success(isEdit ? "Notation mise à jour." : "Notation enregistrée.");
      onSaved();
    } catch (e) { notify.error(fmtError(e)); }
    finally { setSaving(false); }
  };

  const cats = scale?.issuer_categories || {};

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-center justify-center p-4" onClick={onClose}>
      <div className="noam-card w-full max-w-2xl max-h-[92vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()} data-testid="rating-modal">
        <div className="flex items-center justify-between mb-4">
          <div className="text-base font-semibold noam-display">{isEdit ? `Réviser : ${initial.subject_name}` : "Nouvelle notation crédit"}</div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white" data-testid="rating-modal-close"><X className="w-4 h-4" /></button>
        </div>

        {!isEdit && (
          <>
            <div className="flex gap-2 mb-3">
              {[["org", "Mon organisation", Building2], ["annuaire", "Acteur de l'Annuaire", Search], ["libre", "Saisie libre", User]].map(([v, label, Icon]) => (
                <button key={v} onClick={() => { setSubjectType(v); setSubjectId(null); if (v !== "libre") setSubjectName(""); }}
                  className={`flex-1 flex items-center justify-center gap-1.5 text-xs px-2 py-2 rounded-md border transition ${subjectType === v ? "border-[#378ADD]/60 bg-[#378ADD]/15 text-[#378ADD]" : "border-white/10 text-zinc-400 hover:text-white"}`}
                  data-testid={`rating-subject-${v}`}>
                  <Icon className="w-3.5 h-3.5" /> {label}
                </button>
              ))}
            </div>
            {subjectType === "annuaire" && (
              <div className="relative mb-3">
                <input value={subjectId ? subjectName : query} onChange={(e) => { setSubjectId(null); setSubjectName(""); setQuery(e.target.value); }}
                  placeholder="Rechercher un acteur…" className="noam-input w-full" data-testid="rating-annuaire-search" />
                {results.length > 0 && !subjectId && (
                  <div className="absolute z-10 w-full mt-1 bg-[#0A0A0A] border border-white/10 rounded-md shadow-xl">
                    {results.map((e) => (
                      <button key={e.id} onClick={() => { setSubjectId(e.id); setSubjectName(e.name); setResults([]); }}
                        className="w-full text-left px-3 py-2 text-xs text-zinc-200 hover:bg-white/5" data-testid={`rating-annuaire-pick-${e.id}`}>
                        {e.name} <span className="text-zinc-500">· {e.sector || e.type}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            {subjectType === "libre" && (
              <input value={subjectName} onChange={(e) => setSubjectName(e.target.value)} placeholder="Nom de l'entité à noter"
                className="noam-input w-full mb-3" data-testid="rating-subject-name" />
            )}
            <label className="block text-[11px] text-zinc-400 mb-1">Typologie d'émetteur (méthodologie Bloomfield)</label>
            <select value={issuerCategory} onChange={(e) => setIssuerCategory(e.target.value)} className="noam-input w-full mb-3" data-testid="rating-issuer-category">
              {Object.entries(cats).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </>
        )}

        <label className="block text-[11px] text-zinc-400 mb-1">Contexte pour l'analyse (finances, gouvernance, environnement, dette…)</label>
        <textarea value={context} onChange={(e) => setContext(e.target.value)} rows={4} maxLength={6000}
          placeholder="Ex : CA 2025 de 4,2 Mds FCFA (+12%), endettement net/EBITDA 2,1x, actionnaire majoritaire étatique, secteur télécom UEMOA en consolidation…"
          className="noam-input w-full mb-3 text-sm" data-testid="rating-context" />

        <button onClick={runAssess} disabled={assessing}
          className="w-full mb-4 flex items-center justify-center gap-2 text-xs px-3 py-2.5 rounded-md border border-[#7F77DD]/50 bg-[#7F77DD]/10 text-[#B5AEF7] hover:bg-[#7F77DD]/20 transition disabled:opacity-50"
          data-testid="rating-ai-assess-btn">
          {assessing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          {assessing ? "Analyse selon la méthodologie Bloomfield…" : "Analyse IA — pré-remplir la note"}
        </button>

        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className="block text-[11px] text-zinc-400 mb-1">Note (AAA → D)</label>
            <select value={note} onChange={(e) => setNote(e.target.value)} className="noam-input w-full font-mono" data-testid="rating-note-select">
              <option value="">— Choisir —</option>
              <optgroup label="Catégorie investissement">
                {(scale?.notes || []).slice(0, 10).map((n) => <option key={n} value={n}>{n}</option>)}
              </optgroup>
              <optgroup label="Catégorie spéculative">
                {(scale?.notes || []).slice(10).map((n) => <option key={n} value={n}>{n}</option>)}
              </optgroup>
            </select>
          </div>
          <div>
            <label className="block text-[11px] text-zinc-400 mb-1">Perspective</label>
            <select value={outlook} onChange={(e) => setOutlook(e.target.value)} className="noam-input w-full" data-testid="rating-outlook-select">
              <option value="positive">Positive</option>
              <option value="stable">Stable</option>
              <option value="negative">Négative</option>
            </select>
          </div>
        </div>

        {intlEq && (
          <div className="text-[11px] text-zinc-400 mb-3 flex items-center gap-1.5" data-testid="rating-intl-eq">
            <Info className="w-3.5 h-3.5 text-[#378ADD]" /> Équivalent international indicatif : <span className="text-zinc-200">{intlEq}</span>
          </div>
        )}

        <label className="block text-[11px] text-zinc-400 mb-1">Justification</label>
        <textarea value={justification} onChange={(e) => setJustification(e.target.value)} rows={5} maxLength={8000}
          className="noam-input w-full mb-3 text-sm" data-testid="rating-justification" />

        {criteria.length > 0 && (
          <div className="mb-3">
            <div className="text-[11px] text-zinc-400 mb-1.5">Évaluation par critère</div>
            <div className="space-y-1 max-h-44 overflow-y-auto">
              {criteria.map((c, i) => (
                <div key={i} className="flex items-start gap-2 text-[11px] bg-black/30 rounded px-2.5 py-1.5">
                  <span className={`shrink-0 mt-0.5 w-1.5 h-1.5 rounded-full ${c.strength === "fort" ? "bg-[#1D9E75]" : c.strength === "faible" ? "bg-[#E24B4A]" : c.strength === "inconnu" ? "bg-zinc-600" : "bg-[#EF9F27]"}`} />
                  <div className="min-w-0">
                    <span className="text-zinc-300 font-medium">{c.name}</span>
                    <span className="text-zinc-600 ml-1">({c.type})</span>
                    <div className="text-zinc-500">{c.assessment}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {dataGaps.length > 0 && (
          <div className="mb-3 text-[11px] text-[#EF9F27] bg-[#EF9F27]/10 border border-[#EF9F27]/25 rounded px-3 py-2" data-testid="rating-data-gaps">
            <span className="font-medium">Informations manquantes :</span> {dataGaps.join(" · ")}
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-white/5">
          <button onClick={onClose} className="btn-ghost text-xs">Annuler</button>
          <button onClick={save} disabled={saving || !note} className="btn-primary text-xs disabled:opacity-50" data-testid="rating-save-btn">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : isEdit ? "Mettre à jour" : "Enregistrer la notation"}
          </button>
        </div>
      </div>
    </div>
  );
}

function RatingCard({ r, scale, onEdit, onDelete }) {
  const [open, setOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const O = OUTLOOK_META[r.outlook] || OUTLOOK_META.stable;
  const catLabel = scale?.issuer_categories?.[r.issuer_category]?.label || r.issuer_category;

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const resp = await api.get(`/workspace/credit-rating/${r.id}/report.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `notation_${r.subject_name.replace(/[^\w-]+/g, "_")}_${r.note}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { notify.error(fmtError(e)); }
    finally { setDownloading(false); }
  };

  return (
    <div className="noam-card p-4" data-testid={`rating-card-${r.id}`}>
      <div className="flex items-center gap-4">
        <NoteBadge note={r.note} noteClass={r.note_class} />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-white truncate flex items-center gap-2">
            {r.subject_name}
            {r.review_due && (
              <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-[#EF9F27]/15 border border-[#EF9F27]/40 text-[#EF9F27] font-medium" data-testid={`rating-review-due-${r.id}`}>
                <AlertTriangle className="w-3 h-3" /> Revue annuelle due
              </span>
            )}
          </div>
          <div className="text-[11px] text-zinc-500 flex flex-wrap items-center gap-x-2">
            <span>{catLabel}</span>
            <span>·</span>
            <span className={r.category === "investment" ? "text-[#378ADD]" : "text-[#EF9F27]"}>
              {r.category === "investment" ? "Catégorie investissement" : "Catégorie spéculative"}
            </span>
            <span>·</span>
            <span>{r.risk_label}</span>
            {r.age_days != null && <span>· révisée il y a {r.age_days} j</span>}
          </div>
        </div>
        <span className="flex items-center gap-1 text-[11px]" style={{ color: O.color }} title={`Perspective ${O.label}`}>
          <O.icon className="w-3.5 h-3.5" /> {O.label}
        </span>
        <div className="flex items-center gap-1">
          <button onClick={downloadPdf} disabled={downloading} className="p-1.5 rounded hover:bg-white/5 text-zinc-400 hover:text-[#378ADD] disabled:opacity-50" title="Rapport PDF" data-testid={`rating-pdf-${r.id}`}>
            {downloading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5" />}
          </button>
          <button onClick={() => setOpen((v) => !v)} className="p-1.5 rounded hover:bg-white/5 text-zinc-400 hover:text-white" title="Détail" data-testid={`rating-expand-${r.id}`}>
            {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </button>
          <button onClick={() => onEdit(r)} className="p-1.5 rounded hover:bg-white/5 text-zinc-400 hover:text-white" title="Réviser" data-testid={`rating-edit-${r.id}`}>
            <Pencil className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => onDelete(r)} className="p-1.5 rounded hover:bg-white/5 text-zinc-400 hover:text-[#E24B4A]" title="Supprimer" data-testid={`rating-delete-${r.id}`}>
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
      {open && (
        <div className="mt-3 pt-3 border-t border-white/5 space-y-3" data-testid={`rating-detail-${r.id}`}>
          {r.justification && <p className="text-sm text-zinc-300 whitespace-pre-wrap leading-relaxed">{r.justification}</p>}
          {(r.criteria || []).length > 0 && (
            <div className="grid sm:grid-cols-2 gap-1.5">
              {r.criteria.map((c, i) => (
                <div key={i} className="flex items-start gap-2 text-[11px] bg-black/30 rounded px-2.5 py-1.5">
                  <span className={`shrink-0 mt-0.5 w-1.5 h-1.5 rounded-full ${c.strength === "fort" ? "bg-[#1D9E75]" : c.strength === "faible" ? "bg-[#E24B4A]" : c.strength === "inconnu" ? "bg-zinc-600" : "bg-[#EF9F27]"}`} />
                  <div className="min-w-0">
                    <span className="text-zinc-300">{c.name}</span>
                    <div className="text-zinc-500">{c.assessment}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
          {(r.history || []).length > 1 && (
            <div className="text-[11px] text-zinc-500">
              Historique : {r.history.map((h) => `${h.note} (${new Date(h.at).toLocaleDateString("fr-FR")})`).join(" → ")}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function CreditRating() {
  const [scale, setScale] = useState(null);
  const [items, setItems] = useState(null);
  const [reviewDueCount, setReviewDueCount] = useState(0);
  const [modal, setModal] = useState(null); // null | "new" | rating obj

  const load = useCallback(async () => {
    try {
      const r = await api.get("/workspace/credit-rating");
      setItems(r.data.items || []);
      setReviewDueCount(r.data.review_due_count || 0);
    } catch (e) { notify.error(fmtError(e)); }
  }, []);

  useEffect(() => {
    api.get("/workspace/credit-rating/scale").then((r) => setScale(r.data)).catch(() => {});
    load();
  }, [load]);

  const del = async (r) => {
    if (!window.confirm(`Supprimer la notation de « ${r.subject_name} » ?`)) return;
    try {
      await api.delete(`/workspace/credit-rating/${r.id}`);
      notify.success("Notation supprimée.");
      await load();
    } catch (e) { notify.error(fmtError(e)); }
  };

  const stats = useMemo(() => {
    if (!items || items.length === 0) return null;
    const inv = items.filter((i) => i.category === "investment").length;
    return { total: items.length, investment: inv, speculative: items.length - inv };
  }, [items]);

  return (
    <div className="space-y-4" data-testid="credit-rating-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-sm font-semibold text-white">Notation crédit — échelle Bloomfield (UEMOA / CEMAC)</div>
          <p className="text-[11px] text-zinc-500 mt-0.5 max-w-2xl">
            Notez vos contreparties ou votre propre organisation sur l'échelle régionale AAA → D
            (20 crans), avec pré-remplissage IA selon la méthodologie Bloomfield Investment Corporation.
          </p>
        </div>
        <button onClick={() => setModal("new")} className="btn-primary text-xs" data-testid="rating-new-btn">
          <Plus className="w-3.5 h-3.5" /> Nouvelle notation
        </button>
      </div>

      {reviewDueCount > 0 && (
        <div className="flex items-center gap-2 text-xs text-[#EF9F27] bg-[#EF9F27]/10 border border-[#EF9F27]/30 rounded-md px-3 py-2" data-testid="rating-review-banner">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          {reviewDueCount} notation{reviewDueCount > 1 ? "s ont" : " a"} plus d'un an — une revue annuelle est recommandée (pratique Bloomfield).
        </div>
      )}

      {stats && (
        <div className="flex gap-3 text-[11px] text-zinc-400">
          <span>{stats.total} notation{stats.total > 1 ? "s" : ""}</span>
          <span className="text-[#378ADD]">{stats.investment} en catégorie investissement</span>
          <span className="text-[#EF9F27]">{stats.speculative} en catégorie spéculative</span>
        </div>
      )}

      {items === null ? (
        <div className="noam-card p-10 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-zinc-500" /></div>
      ) : items.length === 0 ? (
        <div className="noam-card p-10 text-center" data-testid="rating-empty">
          <Gauge className="w-10 h-10 mx-auto text-[#378ADD] opacity-50" />
          <div className="noam-display text-lg mt-3">Aucune notation pour l'instant</div>
          <p className="text-zinc-500 text-sm mt-1 max-w-md mx-auto">
            Notez une contrepartie de votre Annuaire, un émetteur ou votre propre organisation —
            l'IA pré-remplit la note selon la méthodologie Bloomfield.
          </p>
          <button onClick={() => setModal("new")} className="btn-primary text-xs mt-4 mx-auto inline-flex" data-testid="rating-empty-new">
            <Plus className="w-3.5 h-3.5" /> Première notation
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((r) => (
            <RatingCard key={r.id} r={r} scale={scale} onEdit={(x) => setModal(x)} onDelete={del} />
          ))}
        </div>
      )}

      <ScalePanel scale={scale} />

      {modal && (
        <RatingModal
          scale={scale}
          initial={modal === "new" ? null : modal}
          onClose={() => setModal(null)}
          onSaved={async () => { setModal(null); await load(); }}
        />
      )}
    </div>
  );
}
